import React from "react"
import "./style/featured-campaign.scss"

import classNames from "classnames"
import { showFeaturedCampaignPopup } from "@app/store/app/appActions"
import Icon from "@ng-mw/reol/components/Icon"
import { UserState } from "@ng-mw/shared-react-components/src/modules/user/types"
import oneHalf from "@resources/icons/oneHalf"
import halfPriceNovemberImage from "@resources/images/half-price-november.jpg"
import { useDispatch } from "react-redux"
import SwipeHint from "./SwipeHint"
import Truck from "./Truck"

import { isDebugMode } from "@app/helpers"
import PauseMotionWrapper from "@modules/shared/PauseMotionWrapper"

interface CampaignDateRange {
    startInDebug: Date
    start: Date
    end: Date
}

type Theme = "default" | "xmas"
type StoreMapping = Record<string, true | undefined>

export interface BaseCampaignWrapperProps {
    theme?: Theme
    children: React.ReactNode
    truckPosition: "none" | "first" | "last"
    hasPopupConfig: boolean
}
export const BaseCampaignWrapper = ({
    theme,
    children,
    truckPosition,
    hasPopupConfig,
}: BaseCampaignWrapperProps) => {
    const dispatch = useDispatch<any>()

    const handleClick = () => {
        dispatch(showFeaturedCampaignPopup())
    }

    return (
        <div
            className={classNames("featured-campaign", {
                "featured-campaign--xmas": theme === "xmas",
            })}
            // Only call showFeaturedCampaignPopup if config exists
            onClick={hasPopupConfig ? handleClick : undefined}
            role="button"
        >
            <div className="featured-campaign__content">
                {truckPosition === "first" ? (
                    <div className="featured-campaign__truck-wrapper">
                        <Truck />
                    </div>
                ) : null}
                {children}
                {truckPosition === "last" ? (
                    <div className="featured-campaign__truck-wrapper">
                        <Truck />
                    </div>
                ) : null}
            </div>
            <div className="featured-campaign__cta">
                <SwipeHint
                    type="carousel"
                    showOnlyOncePerSession={false}
                    delay={6000}
                />
            </div>
        </div>
    )
}

export interface CampaignConfigProps {
    theme?: Theme
    /**
     * @example "i desember" or "i januar" or "resten av året"
     */
    period?: string
    hasPopupConfig: boolean
}
export const FreeDelivery = ({ theme, period, hasPopupConfig }: CampaignConfigProps) => {
    return (
        <BaseCampaignWrapper
            theme={theme}
            truckPosition="last"
            hasPopupConfig={hasPopupConfig}
        >
            <>
                <div className="featured-campaign__text">
                    <h2 className="featured-campaign__display-title">
                        <span className="featured-campaign__display-title__line featured-campaign__verybigtext">
                            Gratis
                        </span>
                        <span className="featured-campaign__display-title__line featured-campaign__size-foxtrot">
                            levering {period}
                        </span>
                    </h2>
                </div>
                <div className="featured-campaign__text">
                    <h3 className="featured-campaign__display-title featured-campaign__size-india">
                        <span className="featured-campaign__display-title__line">
                            når du handler over 1000,-
                        </span>
                    </h3>
                </div>
            </>
        </BaseCampaignWrapper>
    )
}

export const FivePercent = ({ theme, period, hasPopupConfig }: CampaignConfigProps) => {
    return (
        <BaseCampaignWrapper
            theme={theme}
            truckPosition="last"
            hasPopupConfig={hasPopupConfig}
        >
            <>
                <div className="featured-campaign__text">
                    <span
                        className="featured-campaign__verybigtext"
                        aria-hidden
                    >
                        5%
                    </span>

                    <h2 className="featured-campaign__display-title featured-campaign__size-bravo">
                        <span className="ws-visually-hidden">Fem prosent </span>
                        <span className="featured-campaign__display-title__line">Trumf-</span>
                        <span className="featured-campaign__display-title__line">bonus</span>
                    </h2>
                </div>
                <div className="featured-campaign__text">
                    <h3 className="featured-campaign__display-title featured-campaign__size-golf">
                        <span className="featured-campaign__display-title__line">
                            på netthandel {period}
                        </span>
                    </h3>
                </div>
            </>
        </BaseCampaignWrapper>
    )
}

export const DeliveryHalfPrice = ({ theme, period, hasPopupConfig }: CampaignConfigProps) => {
    return (
        <BaseCampaignWrapper
            theme={theme}
            truckPosition="last"
            hasPopupConfig={hasPopupConfig}
        >
            <h2 className="featured-campaign__text">
                <Icon
                    icon={oneHalf}
                    size={{ width: "82px", height: "auto" }}
                />
                <span
                    className="featured-campaign__verybigtext"
                    style={{ marginBottom: "-0.15em", marginTop: "0.1em" }}
                >
                    <span className="ws-visually-hidden">Halv </span>
                    pris
                </span>
            </h2>
            <div className="featured-campaign__text">
                <h3 className="featured-campaign__display-title featured-campaign__size-foxtrot">
                    <span className="featured-campaign__display-title__line">
                        på levering {period}
                    </span>
                </h3>
            </div>
            <div className="featured-campaign__text">
                <h3 className="featured-campaign__display-title featured-campaign__size-india">
                    <span className="featured-campaign__display-title__line">
                        når du handler for over 1000,-
                    </span>
                </h3>
            </div>
        </BaseCampaignWrapper>
    )
}

export interface ImageCampaignProps {
    hasPopupConfig: boolean
    imageSrc: string
    /**
     * Should describe the campaign message shown in the image.
     */
    alt: string
}
export const ImageCampaign = ({ hasPopupConfig, imageSrc, alt }: ImageCampaignProps) => {
    const dispatch = useDispatch<any>()

    const handleClick = () => {
        dispatch(showFeaturedCampaignPopup())
    }

    return (
        <div
            className="featured-campaign featured-campaign--image"
            // Only call showFeaturedCampaignPopup if config exists
            onClick={hasPopupConfig ? handleClick : undefined}
            role={hasPopupConfig ? "button" : undefined}
        >
            <img
                className="featured-campaign__image"
                src={imageSrc}
                alt={alt}
            />
        </div>
    )
}

const MlhoGeoStoreGlns: Record<string, true | undefined> = {
    "7080001271923": true, // MENY Bjølsen
    "7080001150488": true, // MENY Bryn
    "7080001150471": true, // MENY Hagaløkkveien
    "7080000909865": true, // MENY Jessheim
    "7080001022945": true, // MENY Kolsås
    "7080004423176": true, // MENY Levert hjem Oslo
    "7080000069743": true, // MENY Mosseporten
    "7080000954674": true, // MENY Osloveien
    "7080001161262": true, // MENY Ringnes Park
    "7080000918737": true, // MENY Skedsmo Senter
    "7080000264131": true, // MENY Ski Storsenter
    "7080000033089": true, // MENY Skjetten
    "7080001296346": true, // MENY Skøyen
    "7080003413727": true, // MENY Strømmen
    "7080001490256": true, // MENY Vika
    "7080000886050": true, // MENY Vollebekk
    "7080001271978": true, // MENY Østfoldhallene
    "7080000954711": true, // MENY Åssiden
}

export const isInDateRange = (range: CampaignDateRange) => {
    const now = new Date()
    const isDebugModeActive = isDebugMode()
    const start = isDebugModeActive ? range.startInDebug : range.start

    return now >= start && now <= range.end
}

export const isGeoFencedCampaignVisible = (
    loggedin: boolean,
    user: UserState,
    geoStoreGlns: StoreMapping,
) => {
    if (!loggedin) {
        return false
    }
    return geoStoreGlns[user.data.store.gln] ?? false
}

export const getCampaignBanners = (loggedin: boolean, user: UserState) => {
    // Remember to update the texts and dates in getCampaignPopupTexts to match
    // any popups here.

    // Just in case we forget to update the config, or it's malconfigured
    // check that a config exists:
    const hasPopupConfig = !!getCampaignPopupTexts(loggedin, user)

    if (
        !user.data.household.isOrganization &&
        isInDateRange({
            startInDebug: new Date("2026-10-28T00:00:00"),
            start: new Date("2026-11-01T00:00:00"), // all of november
            end: new Date("2026-11-30T23:59:59"),
        })
    ) {
        return [
            <ImageCampaign
                key="nov2026"
                hasPopupConfig={hasPopupConfig}
                imageSrc={halfPriceNovemberImage}
                alt="Halv pris på levering i hele november. Gjelder ved kjøp over 1000 kr."
            />,
        ]
    }

    if (
        // isGeoFencedCampaignVisible(loggedin, user, MlhoGeoStoreGlns) &&
        isInDateRange({
            startInDebug: new Date("2026-01-30T00:00:00"), // now
            start: new Date("2026-02-01T00:00:00"), // all of february
            end: new Date("2026-02-28T23:59:59"), //
        })
    ) {
        return [
            <PauseMotionWrapper key="feb2026">
                <DeliveryHalfPrice
                    hasPopupConfig={hasPopupConfig}
                    period="i februar"
                />
            </PauseMotionWrapper>,
        ]
    }

    if (
        isGeoFencedCampaignVisible(loggedin, user, MlhoGeoStoreGlns) &&
        isInDateRange({
            startInDebug: new Date("2026-01-19T00:00:00"),
            start: new Date("2026-04-06T00:00:00"), // 6. april
            end: new Date("2026-04-30T23:59:59"), // end of april
        })
    ) {
        return [
            <PauseMotionWrapper key="apr2026">
                <FivePercent
                    hasPopupConfig={hasPopupConfig}
                    period="i april"
                />
            </PauseMotionWrapper>,
        ]
    }

    if (
        isGeoFencedCampaignVisible(loggedin, user, MlhoGeoStoreGlns) &&
        isInDateRange({
            startInDebug: new Date("2025-11-27T00:00:00"),
            start: new Date("2025-12-01T00:00:00"), // 1. desember
            end: new Date("2025-12-31T23:59:59"), // new years eve
        })
    ) {
        return [
            <PauseMotionWrapper key="dec2025">
                <FreeDelivery
                    hasPopupConfig={hasPopupConfig}
                    period="i desember"
                />
            </PauseMotionWrapper>,
        ]
    }

    return []
}
export interface CampaignPopupConfig {
    image?: {
        url: string
        alt: string
    }
    title: string
    description: React.ReactNode
}

export const getCampaignPopupTexts = (
    loggedin: boolean,
    user: UserState,
): CampaignPopupConfig | undefined => {
    if (
        !user.data.household.isOrganization &&
        isInDateRange({
            startInDebug: new Date("2026-10-28T00:00:00"),
            start: new Date("2026-11-01T00:00:00"), // all of november
            end: new Date("2026-11-30T23:59:59"),
        })
    ) {
        return {
            title: "Halv pris på hjemleveringen",
            description: `I hele november får du halv pris på hjemlevering på all netthandel når du handler for over 1000 kr!  Takk for handelen!`,
        }
    }

    if (
        isInDateRange({
            startInDebug: new Date("2026-01-30T00:00:00"), // now
            start: new Date("2026-02-01T00:00:00"), // all of february
            end: new Date("2026-02-28T23:59:59"), //
        })
    ) {
        return {
            title: "Halv pris på hjemleveringen",
            description: `I hele februar får du halv pris på hjemlevering på all netthandel når du handler for over 1000 kr!  Takk for handelen!`,
        }
    }

    if (
        isGeoFencedCampaignVisible(loggedin, user, MlhoGeoStoreGlns) &&
        isInDateRange({
            startInDebug: new Date("2026-01-19T00:00:00"),
            start: new Date("2026-04-06T00:00:00"), // 6. april
            end: new Date("2026-04-30T23:59:59"), // end of april
        })
    ) {
        return {
            title: "5% Trumf-bonus på netthandel",
            description: `I hele april får du 5% Trumf-bonus på all netthandel i Stor-Oslo (inkludert
            Hønefoss, Østfold og Drammen) Andre bonus-kampanjer, vil komme i tillegg. Takk
            for handelen!`,
        }
    }

    if (
        isGeoFencedCampaignVisible(loggedin, user, MlhoGeoStoreGlns) &&
        isInDateRange({
            startInDebug: new Date("2025-11-27T00:00:00"),
            start: new Date("2025-12-01T00:00:00"), // 1. desember
            end: new Date("2025-12-31T23:59:59"), // new years eve
        })
    ) {
        return {
            title: "Vi spanderer hjemleveringen!",
            description: `I hele desember spanderer vi hjemleveringen på all netthandel i Stor-Oslo
            (inkludert Hønefoss, Østfold og Drammen) Takk for handelen og velkommen igjen!`,
        }
    }

    return undefined
}
